import {
  PERCEPTION_MODELS,
  PERCEPTION_MODEL_TRANSLATION,
  PERCEPTION_MODEL_TRANSLATION_NEW,
} from 'constants/routePaths';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  AllProjectTranslationsQuery,
  type ProjectNormSetRow,
  type TranslationRow,
} from '@graphql/queries/PerceptionModelQueries';
import { Add, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';

import { describeDemographic } from './demographic';

/**
 * One norm set on the landing page, opening onto its translations.
 *
 * Translations load only once it is opened — a workspace can hold many norm
 * sets, and fetching every child list to render a closed page would be one
 * query per norm set for rows nobody has asked to see.
 */

const STATUS_COLOUR: Record<string, 'default' | 'info' | 'success' | 'error'> =
  {
    STARTED: 'default',
    IN_PROGRESS: 'info',
    SUCCESS: 'success',
    FAILED: 'error',
  };

const NormSetAccordion: React.FC<{ normSet: ProjectNormSetRow }> = ({
  normSet,
}) => {
  const history = useHistory();
  const [open, setOpen] = React.useState(false);
  // Opening a norm set is usually about its translations, so that panel starts
  // open rather than asking for a second click to reach the thing wanted.
  const [showTranslations, setShowTranslations] = React.useState(true);

  const { data, loading } = useQuery(AllProjectTranslationsQuery, {
    variables: { projectId: normSet.id },
    skip: !open,
  });
  const translations: TranslationRow[] =
    data?.allPerceptionModelProjectTranslations?.nodes ?? [];

  const path = (t: TranslationRow) =>
    PERCEPTION_MODEL_TRANSLATION.replace(
      ':normSetId',
      String(normSet.id),
    ).replace(':translationId', String(t.id));

  return (
    <Accordion
      disableGutters
      expanded={open}
      onChange={(_e, next) => setOpen(next)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box>
          <Typography variant="subtitle1">{normSet.name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            job {normSet.perceptionModelJobId} · {normSet.anchors?.length ?? 0}{' '}
            anchors · {normSet.normSet?.length ?? 0} products ·{' '}
            {new Date(normSet.createdDate).toLocaleDateString()}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={open && showTranslations}
          onChange={(_e, next) => setShowTranslations(next)}
          TransitionProps={{ unmountOnExit: true }}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <AccordionSummary expandIcon={<ExpandMore fontSize="small" />}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexGrow: 1,
                pr: 1,
              }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Translations
                {!loading && ` · ${translations.length}`}
              </Typography>
              {/* Subtle: a text button inside a summary, not a second primary
                  competing with the page's one accent. */}
              <Button
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={(e) => {
                  // The summary is a toggle; without this the panel closes as
                  // the route changes underneath it.
                  e.stopPropagation();
                  history.push(
                    PERCEPTION_MODEL_TRANSLATION_NEW.replace(
                      ':normSetId',
                      String(normSet.id),
                    ),
                  );
                }}
                sx={{ textTransform: 'none' }}
              >
                Add
              </Button>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            {loading && (
              <Typography variant="body2" sx={{ p: 1.5 }}>
                Loading…
              </Typography>
            )}

            {!loading && translations.length === 0 && (
              <Box sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No translations yet — add one to read this norm set as a
                  particular audience.
                </Typography>
              </Box>
            )}

            {translations.length > 0 && (
              <List dense disablePadding>
                {translations.map((t) => (
                  <ListItemButton
                    key={t.id}
                    divider
                    onClick={() => history.push(path(t))}
                  >
                    <ListItemText
                      primary={t.name}
                      secondary={describeDemographic(t.demographic)}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        noWrap: true,
                      }}
                    />
                    <Chip
                      size="small"
                      label={t.status}
                      color={STATUS_COLOUR[t.status] ?? 'default'}
                      variant={t.status === 'STARTED' ? 'outlined' : 'filled'}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => history.push(`${PERCEPTION_MODELS}/${normSet.id}`)}
            sx={{ textTransform: 'none' }}
          >
            Open norm set
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default NormSetAccordion;
